import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Clock, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import { SectionEyebrow, SectionTitle, FadeIn } from '../components/Animated';
import { api } from '../lib/api';

const ContactPage = () => {
  const { t, isRTL } = useLang();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', service: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState('');

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setErrMsg('');
    try {
      await api.post('/lyo-message.php', form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', company: '', service: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrMsg(err?.response?.data?.detail?.[0]?.msg || err?.message || 'Error');
    }
  };

  return (
    <div data-testid="contact-page" className="relative pt-32 pb-24">
      <div className="absolute inset-0 bg-grid bg-grid-radial pointer-events-none opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <FadeIn><SectionEyebrow>{t('contact.eyebrow')}</SectionEyebrow></FadeIn>
        <SectionTitle className="mt-3">{t('contact.title')}</SectionTitle>
        <FadeIn delay={0.2}>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl">{t('contact.sub')}</p>
        </FadeIn>

        <div className="mt-16 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <FadeIn className="lg:col-span-8">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-zinc-950 p-6 md:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={t('contact.name')} name="name" value={form.name} onChange={onChange} required testid="contact-name" />
                <Field label={t('contact.email')} name="email" type="email" value={form.email} onChange={onChange} required testid="contact-email" />
                <Field label={t('contact.phone')} name="phone" value={form.phone} onChange={onChange} testid="contact-phone" />
                <Field label={t('contact.company')} name="company" value={form.company} onChange={onChange} testid="contact-company" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">{t('contact.service')}</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: 'erp', label: t('contact.service_erp') },
                    { v: 'web', label: t('contact.service_web') },
                    { v: 'other', label: t('contact.service_other') },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`contact-service-${opt.v}`}
                      onClick={() => setForm((f) => ({ ...f, service: opt.v }))}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition ${
                        form.service === opt.v
                          ? 'border-blue-500 bg-blue-600/20 text-white'
                          : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/30'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">{t('contact.message')}</label>
                <textarea
                  required
                  rows={5}
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  className="w-full bg-white/5 border border-white/10 focus:border-blue-500 outline-none rounded-md px-4 py-3 text-white resize-none placeholder:text-zinc-500"
                />
              </div>

              {status === 'success' && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-green-400 bg-green-500/10 border border-green-500/30 rounded-md px-4 py-3" data-testid="contact-success">
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  <span>{t('contact.success')}</span>
                </motion.div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-4 py-3" data-testid="contact-error">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <span>{t('contact.error')} {errMsg ? `· ${errMsg}` : ''}</span>
                </div>
              )}

              <motion.button
                whileTap={{ scale: 0.97 }}
                disabled={status === 'sending'}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-50 transition font-semibold text-white"
              >
                {status === 'sending' ? t('contact.sending') : t('contact.send')}
                <Send className={`w-4 h-4 ${isRTL ? 'rtl-flip' : ''}`} />
              </motion.button>
            </form>
          </FadeIn>

          {/* Info */}
          <FadeIn delay={0.15} className="lg:col-span-4">
            <div className="rounded-2xl border border-white/10 bg-zinc-950 p-6 md:p-8 h-full">
              <h4 className="heading-font text-xs uppercase tracking-[0.25em] font-bold text-blue-500 mb-6">
                {t('contact.info_title')}
              </h4>
              <ul className="space-y-6">
                <Info icon={Mail} label={t('contact.email_label')} value="admin@lyomastech.com" href="mailto:admin@lyomastech.com" testid="info-email" />
                <Info icon={Clock} label={t('contact.hours_label')} value={t('contact.hours_value')} testid="info-hours" />
                <Info icon={MapPin} label={t('contact.location_label')} value={t('contact.location_value')} testid="info-location" />
              </ul>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, name, type = 'text', value, onChange, required, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-zinc-300 mb-2">{label}{required && <span className="text-blue-500"> *</span>}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full h-11 bg-white/5 border border-white/10 focus:border-blue-500 outline-none rounded-md px-4 text-white placeholder:text-zinc-500"
    />
  </div>
);

const Info = ({ icon: Icon, label, value, href, testid }) => (
  <li className="flex gap-3" data-testid={testid}>
    <div className="w-10 h-10 rounded-md bg-blue-600/15 border border-blue-500/30 grid place-items-center shrink-0">
      <Icon className="w-4 h-4 text-blue-400" />
    </div>
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold heading-font">{label}</p>
      {href ? (
        <a href={href} className="block mt-1 text-white hover:text-blue-400 transition font-medium">{value}</a>
      ) : (
        <p className="mt-1 text-white font-medium">{value}</p>
      )}
    </div>
  </li>
);

export default ContactPage;
